import React, { type ChangeEvent } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { DraggableProvided } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { HiOutlineMinusCircle } from 'react-icons/hi'
import { MdDragIndicator } from 'react-icons/md'

import { Box, Flex, IconButton, Select, Text, Tooltip } from '@chakra-ui/react'

import { AiOperatorActionType } from '@/graphql/generated/schemas'

import { AiOperatorModelT, Criterion, CriterionOption } from '../types/types'
import { DEFAULT_MODEL_ID, getModelsForCriterionDropdown } from '../utils/utils'
import { AiOperatorCriterionInput } from './AiOperatorCriterionInput'

const CRITERION_SELECT_WIDTHS = {
  withModelDropdown: {
    maxWidth: {
      base: '180px',
      sm: '220px',
      md: '350px',
      lg: '420px',
      xl: '500px',
      '2xl': '600px',
    },
    // Kept slightly below the natural fit so the row (criteria card + model
    // card) never spills past the form column's right edge
    minWidth: {
      base: '120px',
      sm: '140px',
      md: '170px',
      lg: '220px',
      xl: '270px',
      '2xl': '320px',
    },
  },
  withoutModelDropdown: {
    maxWidth: {
      base: '160px',
      sm: '200px',
      md: '350px',
      lg: '400px',
      xl: '550px',
      '2xl': '700px',
    },
    minWidth: {
      base: '50px',
      sm: '60px',
      md: '100px',
      lg: '120px',
      xl: '150px',
      '2xl': '200px',
    },
  },
} as const

interface AiOperatorCriterionItemIProps {
  item: Criterion
  handleDelete: (id: string) => void
  handleUpdateCriterion: (id: string, newCriterion: CriterionOption) => void
  handleUpdateAction: (id: string, newAction: AiOperatorActionType) => void
  provided: DraggableProvided
  disabled: boolean
  availableCriteria: CriterionOption[]
  aiOperatorModels?: AiOperatorModelT[]
  isImplementer?: boolean
  onAiOperatorModelChange: (
    criterionId: string,
    modelId: string | undefined
  ) => void
}

export const AiOperatorCriterionItem = ({
  item,
  handleDelete,
  handleUpdateCriterion,
  handleUpdateAction,
  provided,
  disabled,
  availableCriteria,
  aiOperatorModels = [],
  isImplementer = false,
  onAiOperatorModelChange,
}: AiOperatorCriterionItemIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()

  const containerBg = disabled ? `bg.disabled.${mode}` : `bg.card.${mode}`
  const controlBg = `bg.input.${mode}`

  const actionOptions = [
    {
      value: AiOperatorActionType.ResolveAndMarkAsFalse,
      label: t('automations:criterion_action_resolve'),
    },
    {
      value: AiOperatorActionType.FlagAndRelease,
      label: t('automations:criterion_action_flag'),
    },
  ]

  const handleCriterionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value
    const selectedCriterion = availableCriteria.find(
      (option) => option.id === selectedValue
    )

    if (selectedCriterion) {
      handleUpdateCriterion(item.id, selectedCriterion)
    }
  }

  const handleActionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedAction = event.target.value as AiOperatorActionType
    handleUpdateAction(item.id, selectedAction)
  }

  const selectedModelId = item.aiOperatorModelId || DEFAULT_MODEL_ID

  const modelsToShow = getModelsForCriterionDropdown(
    item,
    aiOperatorModels,
    disabled
  )

  const shouldShowModelDropdown = isImplementer && modelsToShow.length > 0

  const criterionWidths = shouldShowModelDropdown
    ? CRITERION_SELECT_WIDTHS.withModelDropdown
    : CRITERION_SELECT_WIDTHS.withoutModelDropdown

  return (
    <Flex
      ref={provided.innerRef}
      {...provided.draggableProps}
      alignItems='center'
      gap={1}
      maxWidth='1800px'
      mb={2}
      mx='auto'
      width='100%'
    >
      {/* Main Criteria Container */}
      <Flex
        alignItems='center'
        bg={containerBg}
        borderRadius='10px'
        flex='1'
        justifyContent='space-between'
        minHeight={{ base: '44px', md: '48px', '2xl': '56px' }}
        pl={{ base: '10px', md: '12px', '2xl': '20px' }}
        // No right padding: keeps the delete button close to the model card
        pr={0}
        py={{ base: '10px', md: '12px', '2xl': '16px' }}
      >
        <Flex alignItems='center' flexShrink={0} mr={2}>
          <Box
            {...provided.dragHandleProps}
            alignItems='center'
            display='flex'
            mr={2}
            opacity={disabled ? 0.5 : 1}
            pointerEvents={disabled ? 'none' : 'auto'}
          >
            <MdDragIndicator
              aria-label={t('automations:criterion_drag_handle_aria')}
            />
          </Box>
          <Select
            _focus={{ border: 'none', boxShadow: 'none' }}
            _hover={{ border: 'none' }}
            aria-label={t('automations:criterion_action_type_aria')}
            bg={controlBg}
            border='none'
            borderRadius='lg'
            color={`text.primary.${mode}`}
            isDisabled={disabled}
            maxWidth={{
              base: '60px',
              sm: '80px',
              md: '130px',
              lg: '150px',
            }}
            minWidth={{
              base: '60px',
              sm: '80px',
              md: '130px',
              lg: '150px',
            }}
            onChange={handleActionChange}
            size='sm'
            value={item.action}
          >
            {actionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {/* The criteria action, i.e flag or resolve */}
                {option.label}
              </option>
            ))}
          </Select>
        </Flex>

        <Flex
          alignItems='center'
          flex='1'
          gap={{ base: 1, md: 2, xl: 3, '2xl': 4 }}
          justifyContent='center'
          maxWidth='1200px'
          minWidth='0'
        >
          <Flex
            alignItems='center'
            flexShrink={0}
            justifyContent='center'
            minWidth={{ base: '20px', '2xl': '30px' }}
          >
            <Text
              color={`text.primary.${mode}`}
              fontSize={{ base: 'sm', '2xl': 'md' }}
              textAlign='center'
            >
              {t('automations:criterion_if')}
            </Text>
          </Flex>

          <Select
            _focus={{ border: 'none', boxShadow: 'none' }}
            _hover={{ border: 'none' }}
            aria-label={t('automations:criterion_type_aria')}
            bg={controlBg}
            border='none'
            borderRadius='lg'
            color={`text.primary.${mode}`}
            flex='1'
            isDisabled={disabled}
            isReadOnly
            {...criterionWidths}
            onChange={handleCriterionChange}
            size='sm'
            value={item.criterion.id}
          >
            {availableCriteria.map((option) => (
              <option key={option.id} value={option.id}>
                {/* The criteria description, i.e No Interaction with Door, Door Left Open, etc */}
                {option.description}
              </option>
            ))}
          </Select>

          <Flex
            alignItems='center'
            flexShrink={0}
            justifyContent='center'
            minWidth={{ base: '20px', '2xl': '30px' }}
          >
            <Text
              color={`text.primary.${mode}`}
              fontSize={{ base: 'sm', '2xl': 'md' }}
              textAlign='center'
            >
              {t('automations:criterion_is')}
            </Text>
          </Flex>
        </Flex>

        <Flex alignItems='center' flexShrink={0} ml={2}>
          <AiOperatorCriterionInput
            aria-label={t('automations:criterion_detection_state_aria')}
            isDisabled={disabled}
            showModelDropdown={shouldShowModelDropdown}
            value={
              item.detected
                ? t('automations:criterion_detected')
                : t('automations:criterion_not_detected')
            }
          />
          {!disabled && (
            <Tooltip label={t('automations:criterion_delete_aria')}>
              <IconButton
                _focus={{ border: 'none', boxShadow: 'none' }}
                _hover={{ opacity: 0.7 }}
                aria-label={t('automations:criterion_delete_aria')}
                color={`icon.danger.${mode}`}
                icon={<HiOutlineMinusCircle />}
                ml={{ base: 1, '2xl': 2 }}
                onClick={() => handleDelete(item.id)}
                size='sm'
                variant='ghost'
              />
            </Tooltip>
          )}
        </Flex>
      </Flex>

      {/* AI Model Container */}
      {shouldShowModelDropdown && (
        <Flex
          alignItems='center'
          bg={containerBg}
          borderRadius='10px'
          minHeight={{ base: '44px', md: '48px', '2xl': '56px' }}
          // Shrinkable (down to the select's own minWidth) so the row stays
          // inside the form column instead of overflowing to the right
          minWidth={0}
          // No left padding: keeps the model card close to the delete button
          pl={0}
          pr={{ base: '10px', md: '12px', '2xl': '20px' }}
          py={{ base: '10px', md: '12px', '2xl': '16px' }}
        >
          <Select
            _focus={{ border: 'none', boxShadow: 'none' }}
            _hover={{ border: 'none' }}
            aria-label={t('automations:ai_operator_model_label')}
            bg={controlBg}
            border='none'
            borderRadius='lg'
            color={`text.primary.${mode}`}
            fontSize='sm'
            fontStyle='normal'
            fontWeight='medium'
            isDisabled={disabled}
            maxWidth={{
              base: '85px',
              sm: '100px',
              md: '120px',
              lg: '140px',
              xl: '160px',
              '2xl': '180px',
            }}
            minWidth={{
              base: '70px',
              sm: '85px',
              md: '100px',
              lg: '120px',
              xl: '140px',
              '2xl': '160px',
            }}
            onChange={(e) => onAiOperatorModelChange(item.id, e.target.value)}
            size='sm'
            sx={{
              '& option': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
            value={selectedModelId}
          >
            {modelsToShow.map((model) => {
              const modelLabel =
                model.id === DEFAULT_MODEL_ID
                  ? t('automations:criterion_model_default_label')
                  : model.canonicalName
              return (
                <option key={model.id} title={modelLabel} value={model.id}>
                  {modelLabel}
                </option>
              )
            })}
          </Select>
        </Flex>
      )}
    </Flex>
  )
}
